import React, { useState, useCallback } from 'react';
import { realSignalApi } from '../../services/realsignal-api';

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    backgroundCard: '#1C2E42',
    backgroundWell: '#0A1218',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    accentDark: '#2E4A5A',
    border: '#1C2E42',
    success: '#22C55E',
    warning: '#F59E0B',
};

interface SavedSearch {
    id: string;
    name: string;
    criteria: {
        counties: string[];
        minBeds?: number;
        maxBeds?: number;
        minDelta?: number;
        statuses: string[];
    };
    resultCount: number;
    lastRun: string;
    alertsEnabled: boolean;
}

const mockSavedSearches: SavedSearch[] = [
    {
        id: '1',
        name: 'Harris County High Delta',
        criteria: {
            counties: ['Harris'],
            minDelta: 30,
            statuses: ['Scheduled'],
        },
        resultCount: 24,
        lastRun: '2 hours ago',
        alertsEnabled: true,
    },
    {
        id: '2',
        name: 'Dallas 3+ Beds',
        criteria: {
            counties: ['Dallas'],
            minBeds: 3,
            statuses: ['Scheduled', 'Postponed'],
        },
        resultCount: 18,
        lastRun: '1 day ago',
        alertsEnabled: false,
    },
    {
        id: '3',
        name: 'Multi-County Watch',
        criteria: {
            counties: ['Tarrant', 'Bexar', 'Travis'],
            minDelta: 20,
            statuses: ['Scheduled'],
        },
        resultCount: 42,
        lastRun: '3 hours ago',
        alertsEnabled: true,
    },
];

interface SavedSearchesProps {
    onBack: () => void;
}

export const SavedSearches: React.FC<SavedSearchesProps> = ({ onBack }) => {
    const [searches, setSearches] = useState<SavedSearch[]>(mockSavedSearches);
    const [savingAlertId, setSavingAlertId] = useState<string | null>(null);
    const [savedMessage, setSavedMessage] = useState<string | null>(null);

    const handleToggleAlerts = useCallback(async (searchId: string, currentEnabled: boolean) => {
        setSavingAlertId(searchId);
        setSavedMessage(null);

        setSearches(prev =>
            prev.map(s => (s.id === searchId ? { ...s, alertsEnabled: !currentEnabled } : s))
        );

        try {
            const result = await realSignalApi.updatePreferences({
                notifications: {
                    emailAlerts: !currentEnabled,
                },
            });

            if (result.state === 'success') {
                setSavedMessage('Saved, takes effect on new alerts');
                setTimeout(() => setSavedMessage(null), 3000);
            } else {
                setSearches(prev =>
                    prev.map(s => (s.id === searchId ? { ...s, alertsEnabled: currentEnabled } : s))
                );
            }
        } catch {
            setSearches(prev =>
                prev.map(s => (s.id === searchId ? { ...s, alertsEnabled: currentEnabled } : s))
            );
        } finally {
            setSavingAlertId(null);
        }
    }, []);

    return (
        <div
            style={{
                minHeight: '100vh',
                backgroundColor: colors.background,
                color: colors.textPrimary,
            }}
        >
            <nav
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 24px',
                    backgroundColor: colors.backgroundLight,
                    borderBottom: `1px solid ${colors.border}`,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                        onClick={onBack}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: 'transparent',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '8px',
                            color: colors.textSecondary,
                            cursor: 'pointer',
                            fontSize: '14px',
                        }}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Back to Dashboard
                    </button>
                    <h1 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Saved Searches</h1>
                </div>
                <button
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                        border: 'none',
                        borderRadius: '8px',
                        color: colors.textPrimary,
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                    }}
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                    New Search
                </button>
            </nav>

            <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
                <div
                    style={{
                        display: 'grid',
                        gap: '16px',
                    }}
                >
                    {searches.map(search => (
                        <div
                            key={search.id}
                            style={{
                                backgroundColor: colors.backgroundCard,
                                borderRadius: '12px',
                                padding: '20px',
                                border: `1px solid ${colors.border}`,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: '16px',
                                }}
                            >
                                <div>
                                    <h3
                                        style={{
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            margin: '0 0 4px 0',
                                        }}
                                    >
                                        {search.name}
                                    </h3>
                                    <p
                                        style={{
                                            fontSize: '13px',
                                            color: colors.textMuted,
                                            margin: 0,
                                        }}
                                    >
                                        Last run: {search.lastRun}
                                    </p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <span
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: colors.backgroundWell,
                                            borderRadius: '20px',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: colors.accent,
                                        }}
                                    >
                                        {search.resultCount} results
                                    </span>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-end',
                                            gap: '4px',
                                        }}
                                    >
                                        <label
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '13px',
                                                color: colors.textSecondary,
                                                cursor:
                                                    savingAlertId === search.id
                                                        ? 'wait'
                                                        : 'pointer',
                                                opacity: savingAlertId === search.id ? 0.7 : 1,
                                            }}
                                        >
                                            <span>Alerts</span>
                                            <button
                                                onClick={() =>
                                                    handleToggleAlerts(
                                                        search.id,
                                                        search.alertsEnabled
                                                    )
                                                }
                                                disabled={savingAlertId === search.id}
                                                style={{
                                                    width: '40px',
                                                    height: '22px',
                                                    borderRadius: '11px',
                                                    backgroundColor: search.alertsEnabled
                                                        ? colors.accent
                                                        : colors.backgroundWell,
                                                    border: 'none',
                                                    cursor:
                                                        savingAlertId === search.id
                                                            ? 'wait'
                                                            : 'pointer',
                                                    position: 'relative',
                                                    transition: 'background-color 0.2s',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        top: '2px',
                                                        left: search.alertsEnabled ? '20px' : '2px',
                                                        width: '18px',
                                                        height: '18px',
                                                        borderRadius: '50%',
                                                        backgroundColor: colors.textPrimary,
                                                        transition: 'left 0.2s',
                                                    }}
                                                />
                                            </button>
                                        </label>
                                        {savedMessage && savingAlertId === null && (
                                            <span
                                                style={{
                                                    fontSize: '11px',
                                                    color: colors.success,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {savedMessage}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    marginBottom: '16px',
                                }}
                            >
                                {search.criteria.counties.map(county => (
                                    <span
                                        key={county}
                                        style={{
                                            padding: '4px 10px',
                                            backgroundColor: colors.backgroundWell,
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            color: colors.textSecondary,
                                        }}
                                    >
                                        {county}
                                    </span>
                                ))}
                                {search.criteria.minBeds && (
                                    <span
                                        style={{
                                            padding: '4px 10px',
                                            backgroundColor: colors.backgroundWell,
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            color: colors.textSecondary,
                                        }}
                                    >
                                        {search.criteria.minBeds}+ beds
                                    </span>
                                )}
                                {search.criteria.minDelta && (
                                    <span
                                        style={{
                                            padding: '4px 10px',
                                            backgroundColor: colors.backgroundWell,
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            color: colors.textSecondary,
                                        }}
                                    >
                                        {search.criteria.minDelta}%+ delta
                                    </span>
                                )}
                                {search.criteria.statuses.map(status => (
                                    <span
                                        key={status}
                                        style={{
                                            padding: '4px 10px',
                                            backgroundColor: colors.backgroundWell,
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            color: colors.textSecondary,
                                        }}
                                    >
                                        {status}
                                    </span>
                                ))}
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    gap: '12px',
                                }}
                            >
                                <button
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        backgroundColor: colors.accent,
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: colors.textPrimary,
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                    }}
                                >
                                    Run Search
                                </button>
                                <button
                                    style={{
                                        padding: '10px 16px',
                                        backgroundColor: 'transparent',
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '8px',
                                        color: colors.textSecondary,
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                    }}
                                >
                                    Edit
                                </button>
                                <button
                                    style={{
                                        padding: '10px 16px',
                                        backgroundColor: 'transparent',
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '8px',
                                        color: colors.textSecondary,
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {searches.length === 0 && (
                    <div
                        style={{
                            textAlign: 'center',
                            padding: '60px 20px',
                            backgroundColor: colors.backgroundCard,
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}`,
                        }}
                    >
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={colors.textMuted}
                            strokeWidth="1.5"
                            style={{ marginBottom: '16px' }}
                        >
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                        </svg>
                        <h3
                            style={{
                                fontSize: '18px',
                                fontWeight: 600,
                                margin: '0 0 8px 0',
                                color: colors.textPrimary,
                            }}
                        >
                            No saved searches yet
                        </h3>
                        <p
                            style={{
                                fontSize: '14px',
                                color: colors.textMuted,
                                margin: '0 0 20px 0',
                            }}
                        >
                            Save your search criteria to quickly access them later
                        </p>
                        <button
                            style={{
                                padding: '12px 24px',
                                background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                                border: 'none',
                                borderRadius: '8px',
                                color: colors.textPrimary,
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 500,
                            }}
                        >
                            Create Your First Search
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
